import isEqualDate from '@functions/helpers/datetime/isEqualDate.js';

const convertDayOfWeek = {
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
  0: 'sunday'
};

/**
 *
 * @param time
 * @returns {number}
 */
function getTimeMinutes(time) {
  const [hours, minutes] = time.split(':');
  return hours * 3600 + minutes * 60;
}

/**
 *
 * @param timeZone
 * @returns {Date}
 */
function currentDateWithTimeZone(timeZone = '') {
  const options = {};
  if (timeZone) options.timeZone = timeZone;
  return new Date(new Date().toLocaleString('en-US', options));
}

/**
 *
 * @param timeZone
 * @returns {number}
 */
function getCurrentTimeInSeconds(timeZone = '') {
  let result = new Date();
  if (timeZone) {
    result = new Date(result.toLocaleString('en-US', {timeZone}));
  }
  return result.getHours() * 3600 + result.getMinutes() * 60;
}

/**
 * @param timeZone
 * @param holidays
 * @returns {*}
 */
function isHoliday({timeZone, holidays}) {
  const currentDate = currentDateWithTimeZone(timeZone);
  return holidays.some(holiday => {
    const timeFrom = new Date(holiday.since);
    const timeTo = new Date(holiday.until);
    if (isEqualDate(timeFrom, timeTo)) {
      return isEqualDate(timeFrom, currentDate);
    }
    timeTo.setHours(23, 59, 59, 59);
    return timeFrom <= currentDate && currentDate <= timeTo;
  });
}

/**
 *
 * @param settings
 * @returns {boolean}
 */
export function checkTimeAvailability(settings) {
  const {customTime, isShowBreakTime, typeWorking, isShowHoliday, timeZone, breakTimes} = settings;
  const currentTime = getCurrentTimeInSeconds(timeZone);
  if (isShowHoliday && isHoliday(settings)) return false;
  if (isShowBreakTime) {
    for (const timeBreak of breakTimes) {
      const startTime = getTimeMinutes(timeBreak.startTimeBreak);
      const endTime = getTimeMinutes(timeBreak.endTimeBreak);
      if (startTime <= currentTime && currentTime <= endTime) return false;
    }
  }

  if (typeWorking === 'custom_time') {
    const currentDate = currentDateWithTimeZone(timeZone);
    const day = convertDayOfWeek[currentDate.getDay()];
    const timeOnline = customTime[day];
    if (!timeOnline.isSelect) {
      return false;
    }
    const timeStart = getTimeMinutes(timeOnline.timeStart);
    const timeEnd = getTimeMinutes(timeOnline.timeEnd);
    return timeStart <= currentTime && currentTime <= timeEnd;
  }

  return true;
}

/**
 * Helper function to get current date and time with time zone for AI prompts
 * Provides comprehensive temporal context for business hours and scheduling questions
 * @param {string} timeZone - IANA time zone identifier (defaults to 'UTC')
 * @param {object} businessHoursSettings - Business hours configuration
 * @returns {string} Formatted date/time string with time zone context and availability status
 */
export function getCurrentDateForPrompt({timeZone = 'UTC', businessHoursSettings = {}}) {
  // Create date in specified time zone
  const zonedDate = new Date(new Date().toLocaleString('en-US', {timeZone}));

  // Format components
  const isoDate = zonedDate.toISOString().split('T')[0];
  const dayOfWeek = zonedDate.toLocaleDateString('en-US', {
    weekday: 'long'
  });
  const readableDate = zonedDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const currentTime = zonedDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });

  const availabilityStatus = getAvailabilityStatus({businessHoursSettings, timeZone});
  // const todayHours = getTodayStoreHours(businessHoursSettings, zonedDate);

  // Build comprehensive context prioritizing business-relevant info
  const contextParts = [
    `- Today: ${dayOfWeek}`,
    `- Current Date: ${isoDate} (${readableDate})`,
    `- Current Time: ${currentTime} ${timeZone}`,
    `- Time Zone: ${timeZone}`,
    `- Staff availability: ${availabilityStatus}`
  ].filter(Boolean);

  return contextParts.join('\n');
}

// Check availability status if business hours settings are provided
function getAvailabilityStatus({timeZone = 'UTC', businessHoursSettings = {}}) {
  if (Object.keys(businessHoursSettings).length === 0) {
    return 'Not configured | AI agent available 24/7';
  }

  const settings = {...businessHoursSettings, timeZone};

  if (checkTimeAvailability(settings)) {
    return 'Online | AI agent available 24/7';
  }

  const reason = getCloseReason(settings);
  return `Offline (${reason}) | AI agent available 24/7`;
}

// Determine why the store is closed
function getCloseReason(settings) {
  if (settings.isShowHoliday && isHoliday(settings)) {
    return 'holiday/special closure';
  }

  if (settings.isShowBreakTime) {
    const currentTimeSeconds = getCurrentTimeInSeconds(settings.timeZone);
    const breakTimes = settings.breakTimes || [];
    for (const timeBreak of breakTimes) {
      const startTime = getTimeMinutes(timeBreak.startTimeBreak);
      const endTime = getTimeMinutes(timeBreak.endTimeBreak);
      if (startTime <= currentTimeSeconds && currentTimeSeconds <= endTime) {
        return 'temporarily closed/break time';
      }
    }
  }

  return 'outside operating hours';
}

/**
 * Get today's store hours for display in AI context
 * @param {object} businessHoursSettings - Business hours configuration
 * @param {Date} currentDate - Current date object
 * @returns {string} Today's store hours or null if not applicable
 */
function getTodayStoreHours(businessHoursSettings = {}, currentDate) {
  if (!businessHoursSettings.customTime || businessHoursSettings.typeWorking !== 'custom_time') {
    return null;
  }

  const {customTime, isShowBreakTime, breakTimes = []} = businessHoursSettings;
  const dayOfWeek = convertDayOfWeek[currentDate.getDay()];
  const todaySettings = customTime[dayOfWeek];

  if (!todaySettings?.isSelect) {
    return 'Closed today';
  }

  let hoursText = `${formatTime(todaySettings.timeStart)} - ${formatTime(todaySettings.timeEnd)}`;

  // Add break times if configured
  if (isShowBreakTime && breakTimes.length > 0) {
    const breakTexts = breakTimes.map(
      bt => `Break ${formatTime(bt.startTimeBreak)}-${formatTime(bt.endTimeBreak)}`
    );
    hoursText += ` (${breakTexts.join(', ')})`;
  }

  return hoursText;
}

/**
 * Format time from 24h to 12h format
 * @param {string} time24 - Time in HH:MM format
 * @returns {string} Time in 12h format
 */
function formatTime(time24) {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes}${ampm}`;
}
